import { Col, Row, Spin } from "antd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ExportProfilePage from "radar/pages/artist-profile/export-profile/ExportProfilePage";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, Charts } from "shared/components";
import ArtistComparator from "../ArtistComparator";
import { LoadingOutlined } from "@ant-design/icons";

const antIcon = (
  <LoadingOutlined
    style={{ fontSize: 48, color: "#ececec" }}
    spin
    className="spin-ref"
  />
);

interface ExportComparisonProps {
  close: () => void;
  series: any;
  categories: any;
  min: number;
  max: number;
  history: any;
}

const htmlToCanvasToPdf = async (
  fileName: string = "profile",
  callback: () => void
) => {
  const doc = new jsPDF("p", "mm", "a4");
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const page1 = document.getElementById("page-1");
  if (page1) {
    const canvaPage1 = await html2canvas(page1, { imageTimeout: 6000 });

    doc.addImage(canvaPage1, "MPEG", 0, 0, width, height);
    doc.save(`${fileName}.pdf`);
    callback();
  }
};

const ExportComparison: React.FC<ExportComparisonProps> = (props) => {
  const { series, categories, min, max, history, close } = props;

  const { t } = useTranslation("radar");

  useEffect(() => {
    const fileName = `STRM_COMPARISON_REPORT `;
    setTimeout(() => {
      htmlToCanvasToPdf(fileName, () => {
        close();
      });
    }, 3000);
  }, [close]);

  return (
    <Spin
      spinning={true}
      tip={t("pages.Comparison.loadingMessage")}
      indicator={antIcon}
    >
      <div style={{ margin: 0 }} className="export-comparison">
        <ExportProfilePage
          id="page-1"
          page={1}
          className="artist-comparison"
          title={t("pages.Comparison.exportTitle")}
        >
          <div className="export-comparison-content">
            <Row gutter={[24, 4]} justify="center">
              <Col xs={24}>
                <ArtistComparator exporting />
              </Col>
              <h1 className="page-title">
                {t("pages.ArtistProfile.performanceIndex")}
              </h1>
              <span className="disclaimer">
                {t("pages.Comparison.exportDisclaimer")}
              </span>
              <Col xs={24}>
                <Charts.LineChart
                  color={[
                    "#E7E0C9",
                    "#5C8959",
                    "#6C7AA1",
                    "#12C5D0",
                    "#FFADA7",
                  ]}
                  tickAmount={10}
                  min={min}
                  max={max}
                  height={380}
                  {...history}
                  xAxisFormatter={(val) => (val || 0).toPrecision(3)}
                  animation={false}
                />
              </Col>
              <h1 className="page-title">
                {t("pages.ArtistProfile.strmIndex")}
              </h1>
              <Col xs={24}>
                <Card>
                  <div style={{ width: "100%", margin: 12 }}>
                    <Charts.BarChart
                      cor={[
                        "#E7E0C9",
                        "#5C8959",
                        "#6C7AA1",
                        "#12C5D0",
                        "#FFADA7",
                      ]}
                      series={series}
                      horizontal={false}
                      min={0}
                      max={100}
                      legend={{
                        show: true,
                        fontSize: "18px",
                        position: "top",
                        fontFamily: "Cabin",
                        itemMargin: { horizontal: 32 },
                        markers: {
                          width: 16,
                          height: 16,
                          radius: 50,
                          offsetX: -8,
                        },
                        labels: {
                          useSeriesColors: true,
                        },
                      }}
                      strokeDashArray={7}
                      categories={categories}
                      height={330}
                      showGridY
                    />
                  </div>
                </Card>
              </Col>
              <Col>
                <Row gutter={[16, 32]} justify="center">
                  <Col xs={24}>
                    <span style={{ display: "block" }}>
                      <b style={{ color: "#36BFFA" }}>
                        {t("pages.Radar.performance")}
                      </b>
                    </span>
                    <span style={{ display: "block" }}>
                      {t("pages.Radar.performanceExplaination")}
                    </span>

                    <span style={{ display: "block", marginTop: 8 }}>
                      <b style={{ color: "#F670C7" }}>
                        {t("pages.Radar.development")}
                      </b>
                    </span>
                    <span style={{ display: "block" }}>
                      {t("pages.Radar.developmentExplaination")}
                    </span>

                    <span style={{ display: "block", marginTop: 8 }}>
                      <b style={{ color: "#F97066" }}>
                        {t("pages.Radar.music")}
                      </b>
                    </span>
                    <span style={{ display: "block" }}>
                      {t("pages.Radar.musicExplaination")}
                    </span>

                    <span style={{ display: "block", marginTop: 8 }}>
                      <b style={{ color: "#32D583" }}>
                        {t("pages.Radar.image")}
                      </b>
                    </span>
                    <span style={{ display: "block" }}>
                      {t("pages.Radar.imageExplaination")}
                    </span>

                    <span style={{ display: "block", marginTop: 8 }}>
                      <b style={{ color: "#FDB022" }}>
                        {t("pages.Radar.stability")}
                      </b>
                    </span>
                    <span style={{ display: "block" }}>
                      {t("pages.Radar.stabilityExplaination")}
                    </span>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </ExportProfilePage>
      </div>
    </Spin>
  );
};

export default ExportComparison;
